import React, { useEffect, useState } from 'react';
import { FileText, Clock, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { listPortalInvoices, listPortalMatterPayments, listPortalMatterCharges, listPortalMatters, getPortalInvoice } from '../../lib/db';
import { formatDate, formatMoney } from '../../lib/formatters';
import { printInvoice } from '../../lib/printInvoice';
import { usePortal } from '../../contexts/PortalContext';
import toast from '../../lib/toast';

// Client-facing status labels and styles
const STATUS_STYLE = {
  sent:    { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',         label: 'Pending Payment', icon: Clock },
  overdue: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',                 label: 'Overdue',         icon: AlertCircle },
  paid:    { cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', label: 'Paid',            icon: CheckCircle },
};

const SummaryCard = ({ label, value, tone, sub }) => (
  <div className={`rounded-2xl border p-4 ${tone}`}>
    <p className="text-xs font-medium uppercase tracking-wide opacity-70 mb-1">{label}</p>
    <p className="text-2xl font-bold">{value}</p>
    {sub && <p className="text-xs opacity-60 mt-1">{sub}</p>}
  </div>
);

export default function PortalInvoicesPage() {
  const { contact, firm } = usePortal();
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [charges,  setCharges]  = useState([]);
  const [matters,  setMatters]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (inv) => {
    setDownloading(inv.id);
    const { data, error } = await getPortalInvoice(inv.id);
    setDownloading(null);
    if (error || !data) {
      toast.error('Could not load invoice for download.');
      return;
    }
    printInvoice({
      ...data,
      firm:   firm || {},
      client: contact || {},
    });
  };

  useEffect(() => {
    Promise.all([
      listPortalInvoices(),
      listPortalMatterPayments(),
      listPortalMatterCharges(),
      listPortalMatters(),
    ]).then(([inv, pay, chrg, mat]) => {
      setInvoices(inv.data  || []);
      setPayments(pay.data  || []);
      setCharges(chrg.data  || []);
      setMatters(mat.data   || []);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div>
          <div className="h-8 w-1/3 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          <div className="h-4 w-1/2 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
              <div className="h-7 w-28 bg-slate-200 dark:bg-slate-700 rounded mb-1" />
              <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          ))}
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
            <div className="h-3 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  // ── Billing totals from matter records (source of truth) ─────────────────
  const totalAgreedFee = matters.reduce((s, m) => s + Number(m.flat_fee_amount || m.agreed_fee || 0), 0);
  const totalCharges   = charges.reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalBilled    = totalAgreedFee + totalCharges;
  const totalPaid      = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalDue       = Math.max(0, totalBilled - totalPaid);

  const pendingInvoices = invoices.filter((i) => i.status === 'sent' || i.status === 'overdue');
  const paidInvoices    = invoices.filter((i) => i.status === 'paid');

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Invoices & Payments</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Your billing history and outstanding balances.</p>
      </div>

      {/* ── Summary cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          label="Balance due"
          value={formatMoney(totalDue)}
          sub={totalDue > 0 ? `${pendingInvoices.length} pending invoice${pendingInvoices.length !== 1 ? 's' : ''}` : 'All settled — thank you!'}
          tone={totalDue > 0
            ? 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200'
            : 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-200'}
        />
        <SummaryCard
          label="Total paid"
          value={formatMoney(totalPaid)}
          sub={`${payments.length} payment${payments.length !== 1 ? 's' : ''} recorded`}
          tone="bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-200"
        />
        <SummaryCard
          label="Total billed"
          value={formatMoney(totalBilled)}
          sub={totalBilled > 0 ? `${Math.min(100, Math.round((totalPaid / totalBilled) * 100))}% paid` : '—'}
          tone="bg-slate-50 border-slate-200 text-slate-900 dark:bg-slate-800/40 dark:border-slate-700 dark:text-slate-100"
        />
      </div>

      {/* ── Pending / Overdue invoices ────────────────────────────────────── */}
      {pendingInvoices.length > 0 && (
        <section>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <Clock size={16} className="text-amber-500" /> Pending Invoices
          </h2>
          <div className="space-y-3">
            {pendingInvoices.map((inv) => {
              const s = STATUS_STYLE[inv.status] || STATUS_STYLE.sent;
              const Icon = s.icon;
              const isOverdue = inv.status === 'overdue';
              return (
                <div
                  key={inv.id}
                  className={`rounded-2xl border p-5 ${isOverdue
                    ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800'
                    : 'bg-white border-slate-200 dark:bg-slate-900 dark:border-slate-800'}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium inline-flex items-center gap-1 ${s.cls}`}>
                          <Icon size={11} /> {s.label}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                        Invoice #{inv.invoice_number || inv.id.slice(0, 8)}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {inv.matter?.title || '—'}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {formatMoney(inv.total || 0)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Due {formatDate(inv.due_date) || '—'}
                      </p>
                      <button
                        onClick={() => handleDownload(inv)}
                        disabled={downloading === inv.id}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition disabled:opacity-50"
                      >
                        <Download size={12} /> {downloading === inv.id ? 'Loading…' : 'PDF'}
                      </button>
                    </div>
                  </div>
                  {inv.notes && (
                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800 pt-3">
                      {inv.notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Payment history ───────────────────────────────────────────────── */}
      {payments.length > 0 && (
        <section>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-500" /> Payment History
          </h2>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-5 py-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                      {p.payment_label || 'Payment received'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {p.payment_date ? formatDate(p.payment_date) : '—'}
                      {p.method ? ` · ${p.method}` : ''}
                      {p.receipt_number ? ` · Receipt #${p.receipt_number}` : ''}
                    </p>
                  </div>
                  <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    + {formatMoney(p.amount)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between px-5 py-3 bg-emerald-50 dark:bg-emerald-900/20 border-t border-emerald-100 dark:border-emerald-800">
              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Total paid</p>
              <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">{formatMoney(totalPaid)}</p>
            </div>
          </div>
        </section>
      )}

      {/* ── Paid invoices ─────────────────────────────────────────────────── */}
      {paidInvoices.length > 0 && (
        <section>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white mb-3">Paid Invoices</h2>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {paidInvoices.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between px-5 py-4 gap-4">
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                      Invoice #{inv.invoice_number || inv.id.slice(0, 8)}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {inv.matter?.title || '—'} · Issued {formatDate(inv.invoice_date || inv.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 whitespace-nowrap">
                      Paid
                    </span>
                    <button
                      onClick={() => handleDownload(inv)}
                      disabled={downloading === inv.id}
                      title="Download PDF"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition disabled:opacity-50"
                    >
                      <Download size={12} /> {downloading === inv.id ? 'Loading…' : 'PDF'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {invoices.length === 0 && payments.length === 0 && (
        <div className="text-center py-20 text-slate-400">
          <FileText size={40} className="mx-auto mb-4 opacity-30" />
          <p>No invoices or payments on record yet.</p>
        </div>
      )}
    </div>
  );
}
